import type { SavedChest } from "../core/save";
import { isItem, stackLimit, type ItemStack } from "../world/items";
import { clickStack, type Inventory } from "./inventory";

/**
 * Per-block chest state and the session-wide registry.
 *
 * A chest is a plain 27-slot container with no ticking. The registry key is the
 * block position, so closing the screen and walking away keeps the contents
 * intact until the chest is broken or the world is disposed.
 */

export const CHEST_SIZE = 27;

export class ChestState {
  readonly slots: (ItemStack | null)[] = new Array(CHEST_SIZE).fill(null);

  slotAt(index: number): ItemStack | null {
    return this.slots[index] ?? null;
  }

  /** Apply the shared inventory click rules to a chest slot. */
  clickSlot(
    index: number,
    button: "left" | "right",
    held: ItemStack | null,
  ): ItemStack | null {
    return clickStack(this.slots, index, button, held);
  }

  /** Shift-click a chest slot back into the player's pack. */
  quickMove(index: number, inventory: Inventory): boolean {
    const stack = this.slots[index] ?? null;
    if (!stack) return false;
    this.slots[index] = null;
    inventory.addStack(stack);
    return true;
  }

  /**
   * Shift-click a pack stack into the chest, merging into matching stacks
   * before filling empty slots. Returns how many items moved, so the caller
   * can leave a partial remainder in the player slot.
   */
  insert(stack: ItemStack): number {
    const count = Math.floor(stack.count);
    if (!isItem(stack.id) || !Number.isFinite(stack.count) || count <= 0) return 0;

    const limit = stackLimit(stack.id);
    let left = count;

    if (limit > 1) {
      for (let i = 0; i < this.slots.length && left > 0; i++) {
        const target = this.slots[i];
        if (!target || target.id !== stack.id) continue;
        const room = limit - target.count;
        if (room <= 0) continue;
        const moved = Math.min(room, left);
        this.slots[i] = { ...target, count: target.count + moved };
        left -= moved;
      }
    }

    for (let i = 0; i < this.slots.length && left > 0; i++) {
      if (this.slots[i]) continue;
      const moved = Math.min(limit, left);
      this.slots[i] = { ...stack, count: moved };
      left -= moved;
    }

    return count - left;
  }

  /** Remove and return every stack, used when the chest block is broken. */
  drain(): ItemStack[] {
    const out: ItemStack[] = [];
    for (let i = 0; i < this.slots.length; i++) {
      const stack = this.slots[i];
      if (stack) out.push(stack);
      this.slots[i] = null;
    }
    return out;
  }
}

/** Chest states for the current world, keyed by block position. */
export class ChestBook {
  private readonly states = new Map<string, ChestState>();

  at(x: number, y: number, z: number): ChestState {
    const key = `${x},${y},${z}`;
    let chest = this.states.get(key);
    if (!chest) {
      chest = new ChestState();
      this.states.set(key, chest);
    }
    return chest;
  }

  remove(x: number, y: number, z: number): ChestState | null {
    const key = `${x},${y},${z}`;
    const chest = this.states.get(key) ?? null;
    this.states.delete(key);
    return chest;
  }

  /** Non-empty chests, ready for the save file. */
  snapshot(): SavedChest[] {
    const out: SavedChest[] = [];
    for (const [key, chest] of this.states) {
      if (!chest.slots.some((stack) => stack !== null)) continue;
      const [x, y, z] = key.split(",").map(Number);
      out.push({
        x,
        y,
        z,
        slots: chest.slots.map((stack) => (stack ? { ...stack } : null)),
      });
    }
    return out;
  }

  /** Replace every chest with the saved contents for its position. */
  restore(entries: readonly SavedChest[]): void {
    this.states.clear();
    for (const entry of entries) {
      const chest = this.at(entry.x, entry.y, entry.z);
      for (let i = 0; i < chest.slots.length; i++) {
        const stack = entry.slots[i] ?? null;
        chest.slots[i] = stack ? { ...stack } : null;
      }
    }
  }

  clear(): void {
    this.states.clear();
  }
}
